import { Alert, Button, Card, Checkbox, Grid, Group, Radio, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SAMPLE_PACK_LABELS, SAMPLE_PACK_TYPES, type ExporterDto, type FileRefDto, type RequestViewDto, type SamplePackType } from '@mexp/shared';
import { errorMessage, http } from '@/api/client';
import { PageHeader } from '@/components/PageHeader';
import { FileUploader } from '@/features/files/FileUploader';
import { toBusinessDate } from '@/lib/format';
import { notifySuccess } from '@/lib/notify';
import { drKeys } from './api';
import {
  BdsReviewerSelect,
  CategorySelect,
  ChecklistEditor,
  checklistPayload,
  ChecklistRow,
  CountrySelect,
  DosageFormSelect,
  emptyChecklistRow,
  ExporterSelect,
} from './components/RequestFields';
import { TaskRow, taskRowErrors, TaskRowsEditor, taskRowsToPayload } from './components/TaskRowsEditor';

/** RA/MIS raise a request here; BD (SC-NBD) raises theirs in BDS and it arrives in MExP on its own. */
export function NewRequestPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [checklist, setChecklist] = useState<ChecklistRow[]>([emptyChecklistRow()]);
  const [references, setReferences] = useState<FileRefDto[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [busy, setBusy] = useState(0);
  const [showTaskErrors, setShowTaskErrors] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      exporterId: null as string | null,
      country: null as string | null,
      genericName: '',
      brandName: '',
      packSize: '',
      category: null as string | null,
      dosageForm: null as string | null,
      requiredBy: null as string | null,
      bdReviewerId: null as string | null,
      notes: '',
      sample: false,
      packType: null as SamplePackType | null,
    },
    validate: {
      exporterId: (v) => (v ? null : 'Choose the exporter'),
      country: (v) => (v ? null : 'Choose the country'),
      genericName: (v) => (v.trim() ? null : 'Enter the generic name'),
      packType: (v, values) => (values.sample && !v ? 'Choose a plain or a commercial sample' : null),
    },
  });

  // BD sends a sample and updates it in BDS: the reviewer chosen below, else the client's BD contact.
  const exporterId = form.values.exporterId;
  const exporter = useQuery({
    queryKey: ['exporters', 'detail', exporterId],
    queryFn: () => http.get<ExporterDto>(`/exporters/${exporterId}`),
    enabled: !!exporterId && form.values.sample,
  });
  const bdContact = exporter.data?.bdContact ?? null;
  const needsReviewer = form.values.sample && !form.values.bdReviewerId && !!exporter.data && !bdContact;

  // Tasks can point at checklist items; the server numbers them rd-1, rd-2… in order.
  const namedChecklist = checklist.filter((r) => r.name.trim());
  const checklistForTasks = namedChecklist.map((r, i) => ({ key: `rd-${i + 1}`, name: r.name.trim() }));

  const create = useMutation({
    mutationFn: (values: typeof form.values) =>
      http.post<RequestViewDto>('/dossier-requests', {
        exporterId: values.exporterId,
        country: values.country,
        genericName: values.genericName.trim(),
        brandName: values.brandName.trim() || undefined,
        packSize: values.packSize.trim() || undefined,
        category: values.category ?? undefined,
        dosageForm: values.dosageForm ?? undefined,
        requiredBy: values.requiredBy ?? undefined,
        bdReviewerId: values.bdReviewerId ?? undefined,
        notes: values.notes.trim() || undefined,
        requiredDocs: checklistPayload(checklist),
        referenceFileIds: references.map((f) => f.id),
        workItems: tasks.length ? taskRowsToPayload(tasks) : undefined,
        sample: values.sample && values.packType ? { packType: values.packType } : undefined,
      }),
    onSuccess: async (view) => {
      await queryClient.invalidateQueries({ queryKey: drKeys.all });
      notifySuccess(`${view.request.code} created.`);
      navigate(`/internal/requests/${view.request.id}`, { replace: true });
    },
    onError: (e) => {
      setError(errorMessage(e));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  const submit = form.onSubmit((values) => {
    setError(null);
    if (needsReviewer) {
      form.setFieldError('bdReviewerId', 'Choose the SC-NBD reviewer: BD updates the sample in BDS, and this client has no BD contact yet');
      return;
    }
    if (tasks.some((t) => taskRowErrors(t).length)) {
      setShowTaskErrors(true);
      setError('Complete or remove the unfinished tasks below.');
      return;
    }
    create.mutate(values);
  });

  return (
    <form onSubmit={submit} noValidate>
      <PageHeader
        crumbs={[{ label: 'Document requests', to: '/internal/requests' }, { label: 'New' }]}
        title="New document request"
        description="For requests RA or MIS raise themselves. BD raises its requests in BDS (SC-NBD); they appear here automatically."
      />
      <Stack gap="lg" maw={1040}>
        {error && (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>
            {error}
          </Alert>
        )}
        <Card>
          <Stack gap="md">
            <Title order={4}>Product and market</Title>
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <ExporterSelect {...form.getInputProps('exporterId')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CountrySelect {...form.getInputProps('country')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput label="Generic name" placeholder="e.g. Persea extract" withAsterisk {...form.getInputProps('genericName')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput label="Brand name" placeholder="Optional" {...form.getInputProps('brandName')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput label="Pack size" placeholder="e.g. 3 × 10 capsules" {...form.getInputProps('packSize')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <CategorySelect {...form.getInputProps('category')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <DosageFormSelect {...form.getInputProps('dosageForm')} />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>Documents needed</Title>
              <Text fz="sm" c="dimmed">
                The checklist for this dossier.
              </Text>
            </Stack>
            <ChecklistEditor rows={checklist} onChange={setChecklist} />
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>Sample</Title>
              <Text fz="sm" c="dimmed">
                {/* Optional. BD sends the sample and updates it in BDS, including a commercial sample's artwork; you follow it here. */}
              </Text>
            </Stack>
            <Checkbox
              label="Send the client a sample"
              checked={form.values.sample}
              onChange={(e) => {
                form.setFieldValue('sample', e.currentTarget.checked);
                if (!e.currentTarget.checked) form.setFieldValue('packType', null);
              }}
            />
            {form.values.sample && (
              <>
                <Radio.Group label="Packaging" withAsterisk {...form.getInputProps('packType')}>
                  <Group gap="xl" mt={8}>
                    {SAMPLE_PACK_TYPES.map((p) => (
                      <Radio key={p} value={p} label={SAMPLE_PACK_LABELS[p]} />
                    ))}
                  </Group>
                </Radio.Group>
                {form.values.bdReviewerId ? (
                  <Text fz="sm" c="dimmed">
                    The SC-NBD reviewer chosen below updates it in BDS.
                  </Text>
                ) : bdContact ? (
                  <Text fz="sm" c="dimmed">
                    {bdContact.name}
                    {bdContact.teamName ? ` (${bdContact.teamName})` : ''}, the client's BD contact, updates it in BDS.
                  </Text>
                ) : needsReviewer ? (
                  <Text fz="sm" c="orange">
                    This client has no BD contact yet: choose the SC-NBD reviewer below, who updates the sample in BDS.
                  </Text>
                ) : null}
              </>
            )}
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>Reference files</Title>
              <Text fz="sm" c="dimmed">
                Country checklists, customer formats or anything the team should see. Everyone working on this request can open them.
              </Text>
            </Stack>
            <FileUploader area="dr_reference" value={references} onChange={setReferences} onBusyChange={(b) => setBusy((n) => n + (b ? 1 : -1))} />
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <Title order={4}>BD review</Title>
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <BdsReviewerSelect description="Approves or returns the compiled dossier in BDS. You can choose later." {...form.getInputProps('bdReviewerId')} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <DateInput
                  label="Needed by"
                  description="When BD needs the documents"
                  placeholder="Optional"
                  clearable
                  valueFormat="DD MMM YYYY"
                  minDate={new Date()}
                  value={form.values.requiredBy}
                  onChange={(v) => form.setFieldValue('requiredBy', toBusinessDate(v))}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Textarea label="Notes" autosize minRows={2} maxRows={6} {...form.getInputProps('notes')} />
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        <Card>
          <Stack gap="md">
            <Stack gap={2}>
              <Title order={4}>Assign tasks now</Title>
              <Text fz="sm" c="dimmed">
                Optional: you can also assign tasks after creating the request. Each person is notified and sees the task under My tasks.
              </Text>
            </Stack>
            <TaskRowsEditor
              rows={tasks}
              onChange={setTasks}
              requiredDocs={checklistForTasks}
              showErrors={showTaskErrors}
              onBusyChange={(b) => setBusy((n) => n + (b ? 1 : -1))}
            />
          </Stack>
        </Card>

        <Group justify="flex-end">
          <Button variant="default" onClick={() => navigate('/internal/requests')}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending} disabled={busy > 0}>
            {busy > 0 ? 'Waiting for uploads…' : 'Create request'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
